<template>
  <div class="sharpness-report-detail-container">
    <!-- 1. 顶部图像预览 -->
    <div class="report-header-images pdf-atomic-block">
      <div class="labeled-image-container">
        <div class="image-label-wrapper">
          <el-tag type="primary" effect="dark">{{
            $t("report.originalImage")
          }}</el-tag>
        </div>
        <div class="image-wrapper">
          <div v-if="convertingMap.original" class="image-loading">
            <i class="el-icon-loading"></i>
          </div>
          <div v-else-if="conversionErrors.original" class="image-error">
            {{ $t("error.operationFailed") }}
          </div>
          <img
            v-else
            :src="
              getDisplayUrl(
                'original',
                reportResultData && reportResultData.image_url
              )
            "
            crossorigin="anonymous"
            alt="Original Image Preview"
          />
        </div>
      </div>
      <div class="labeled-image-container">
        <div class="image-label-wrapper">
          <el-tag type="primary" effect="dark">{{
            $t("report.roiImage")
          }}</el-tag>
        </div>
        <div class="image-wrapper">
          <div v-if="convertingMap.roi" class="image-loading">
            <i class="el-icon-loading"></i>
          </div>
          <div v-else-if="conversionErrors.roi" class="image-error">
            {{ $t("error.operationFailed") }}
          </div>
          <img
            v-else
            :src="
              getDisplayUrl(
                'roi',
                reportResultData && reportResultData.image_roi_url
              )
            "
            crossorigin="anonymous"
            alt="ROI Image"
          />
        </div>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <!-- 2. 分析结果 (Analysis result) -->
      <h4 id="analysis-result" class="section-title">
        {{ $t("report.analysis_result_title") }}
      </h4>
      <h5 class="sub-section-title">{{ $t("report.MTF_value") }}</h5>
      <div class="chart-container">
        <div class="chart-inner-wrapper">
          <v-chart
            class="echart mtf-chart"
            :option="mtfValueChartOption"
            ref="mtfValueChartOption_Chart"
            autoresize
          />
        </div>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">{{ $t("report.Square_hypotenuse") }}</h5>
      <div class="chart-container">
        <div class="chart-inner-wrapper">
          <v-chart
            class="echart"
            :option="squareGrayScaleChartOption"
            ref="squareGrayScaleChartOption_Chart"
            autoresize
          />
        </div>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <!-- 3. MTF曲线 (Data curves) -->
      <h4 id="data-curves" class="section-title">
        {{ $t("report.Data_curves_title") }}
      </h4>
      <h5 class="sub-section-title">{{ $t("report.MTF_Area") }}</h5>
      <div class="chart-container">
        <div v-if="convertingMap.roi" class="analysis-placeholder">
          <i class="el-icon-loading"></i>
        </div>
        <div
          v-else-if="conversionErrors.roi"
          class="image-error analysis-placeholder"
        >
          {{ $t("error.operationFailed") }}
        </div>
        <img
          v-else
          class="data-curve-image"
          :src="
            getDisplayUrl(
              'roi',
              reportResultData && reportResultData.image_roi_url
            )
          "
          crossorigin="anonymous"
          alt="MTF Area"
        />
      </div>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">{{ $t("report.LSF_gray") }}</h5>
      <div class="chart-container">
        <div class="chart-inner-wrapper">
          <v-chart
            class="echart"
            :option="lsfGrayScaleChartOption"
            ref="lsfGrayScaleChartOption_Chart"
            autoresize
          />
        </div>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">{{ $t("report.LSF_gray_table") }}</h5>
      <!-- 表格 1 -->
      <el-table
        v-if="reportResultData && reportResultData.result.lsf_gray_scale_form"
        :data="reportResultData.result.lsf_gray_scale_form"
        style="width: 100%"
        border
        class="custom-table"
      >
        <el-table-column label="Item" width="80" align="center">
          <template #default="scope">
            {{ scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column prop="mtf50p" label="MTF50p" show-overflow-tooltip />
        <el-table-column prop="mtf50" label="MTF50" show-overflow-tooltip />
        <el-table-column prop="mtf30" label="MTF30" show-overflow-tooltip />
        <el-table-column prop="mtf10" label="MTF10" show-overflow-tooltip />
        <el-table-column
          prop="sharpening_percent"
          label="Sharpening %"
          show-overflow-tooltip
        />
        <el-table-column
          prop="over_shoot_percent"
          label="Overshoot %"
          show-overflow-tooltip
        />
      </el-table>
    </div>

    <div class="pdf-atomic-block">
      <!-- 表格 2 -->
      <el-table
        v-if="reportResultData && reportResultData.result.lsf_gray_scale_form"
        :data="reportResultData.result.lsf_gray_scale_form"
        style="width: 100%"
        border
        class="custom-table"
      >
        <el-table-column label="Item" width="80" align="center">
          <template #default="scope">
            {{ scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column
          prop="under_shoot_percent"
          label="Undershoot %"
          show-overflow-tooltip
        />
        <el-table-column
          prop="roughness"
          label="Roughness"
          show-overflow-tooltip
        />
        <el-table-column
          prop="mtf_at_83_00c_per_mm"
          label="MTF @ 83.00c/mm"
          show-overflow-tooltip
        />
        <el-table-column
          prop="mtf_at_0_125c_per_p"
          label="MTF @ 0.125c/p"
          show-overflow-tooltip
        />
        <el-table-column
          prop="mtf_at_0_25c_per_p"
          label="MTF @ 0.25c/p"
          show-overflow-tooltip
        />
        <el-table-column
          prop="ca_pixels"
          label="CA (Pixels)"
          show-overflow-tooltip
        />
      </el-table>
    </div>

    <!-- ** 回到顶部按钮 ** -->
    <el-backtop
      v-if="!isPrinting"
      :target="backtopTarget"
      :right="40"
      :bottom="40"
    />
  </div>
</template>

<script>
import { backtopMixin } from "@/mixins/backtopMixin";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
} from "echarts/components";
import VChart from "vue-echarts";
import UTIF from "utif";

use([
  CanvasRenderer,
  LineChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
]);

export default {
  name: "SharpnessReportDetail",
  mixins: [backtopMixin],
  components: {
    VChart,
  },
  props: {
    reportData: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      isPrinting: false,
      convertedImageUrls: {
        original: null,
        roi: null,
      },
      convertingMap: {
        original: false,
        roi: false,
      },
      conversionErrors: {},
    };
  },
  computed: {
    reportResultData() {
      return this.reportData;
    },
    baseChartOption() {
      return {
        title: {
          left: "center",
          top: "3%",
          textStyle: { fontWeight: "normal", fontSize: 12 },
        },
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "cross", label: { backgroundColor: "#6a7985" } },
        },
        legend: {
          type: "scroll",
          bottom: 10,
          left: "center",
          itemWidth: 15,
          itemHeight: 10,
          textStyle: {
            padding: [0, 15, 0, 2],
          },
        },
        grid: {
          top: "15%",
          bottom: "16%",
          left: "3%",
          right: "8%",
          containLabel: true,
        },
        xAxis: {
          type: "value",
          nameLocation: "middle",
          nameGap: 35,
          axisLine: { show: true, onZero: false },
          axisTick: { show: true },
          splitLine: { show: true },
        },
        yAxis: {
          type: "value",
          nameLocation: "middle",
          nameGap: 55,
          axisLine: { show: true, onZero: false },
          axisTick: { show: true },
          minorTick: { show: true },
          splitLine: { show: true },
        },
      };
    },
    mtfValueChartOption() {
      if (!this.reportResultData) return {};
      const chartData = this.reportResultData.result.mtf_value_graph;
      const numSeries = chartData.series.length;

      const series = chartData.series.map((s, index) => ({
        name: `ROI${index + 1}`,
        type: "line",
        smooth: true,
        symbol: "none",
        lineStyle: { type: "solid", width: 1 },
        data: s.line,
      }));

      return {
        ...this.baseChartOption,
        color: this.generateDistinctColors(numSeries),
        title: {
          ...this.baseChartOption.title,
          text: "MTF value graph",
        },
        legend: {
          ...this.baseChartOption.legend,
          data: series.map((s) => s.name),
        },
        xAxis: {
          ...this.baseChartOption.xAxis,
          name: "Resolution[Cycles/Pixel]",
          min: chartData.x_range[0],
          max: chartData.x_range[1],
        },
        yAxis: {
          ...this.baseChartOption.yAxis,
          name: "Modulation",
          min: chartData.y_range[0],
          max: chartData.y_range[1],
        },
        series: series,
      };
    },
    squareGrayScaleChartOption() {
      if (!this.reportResultData) return {};
      const chartData =
        this.reportResultData.result.square_hypotenuse_gray_scale_graph;
      const numSeries = chartData.series.length;

      const series = chartData.series.map((s, index) => ({
        name: `ROI${index + 1}`,
        type: "line",
        step: "end",
        symbol: "none",
        lineStyle: { type: "solid", width: 1 },
        data: s.line,
      }));

      return {
        ...this.baseChartOption,
        color: this.generateDistinctColors(numSeries),
        title: {
          ...this.baseChartOption.title,
          text: "Square hypotenuse gray scale",
        },
        legend: {
          ...this.baseChartOption.legend,
          data: series.map((s) => s.name),
        },
        xAxis: {
          ...this.baseChartOption.xAxis,
          name: "Pixels",
          min: chartData.x_range[0],
          max: chartData.x_range[1],
        },
        yAxis: {
          ...this.baseChartOption.yAxis,
          name: "Pixels value",
          min: chartData.y_range[0],
          max: chartData.y_range[1],
        },
        series: series,
      };
    },
    lsfGrayScaleChartOption() {
      if (!this.reportResultData) return {};
      const chartData = this.reportResultData.result.lsf_gray_scale_graph;
      const numSeries = chartData.series.length;

      const series = chartData.series.map((s, index) => ({
        name: `ROI${index + 1}`,
        type: "line",
        smooth: true,
        symbol: "none",
        lineStyle: { type: "solid", width: 1 },
        data: s.line,
      }));

      return {
        ...this.baseChartOption,
        color: this.generateDistinctColors(numSeries),
        title: {
          ...this.baseChartOption.title,
          text: "LSF gray scale",
        },
        legend: {
          ...this.baseChartOption.legend,
          data: series.map((s) => s.name),
        },
        xAxis: {
          ...this.baseChartOption.xAxis,
          name: "Pixels",
          min: chartData.x_range[0],
          max: chartData.x_range[1],
        },
        yAxis: {
          ...this.baseChartOption.yAxis,
          name: "Pixels value",
          min: chartData.y_range[0],
          max: chartData.y_range[1],
        },
        series: series,
      };
    },
  },
  watch: {
    "reportData.image_url": {
      handler(newUrl) {
        this.handleImageSourceChange("original", newUrl);
      },
      immediate: true,
    },
    "reportData.image_roi_url": {
      handler(newUrl) {
        this.handleImageSourceChange("roi", newUrl);
      },
      immediate: true,
    },
  },
  methods: {
    isTifUrl(url) {
      if (!url) return false;
      const lower = String(url).toLowerCase();
      return lower.endsWith(".tif") || lower.endsWith(".tiff");
    },
    getDisplayUrl(type, url) {
      if (!url) return "";
      const converted = this.convertedImageUrls[type];
      if (converted) {
        return converted;
      }
      if (!this.isTifUrl(url)) {
        return url;
      }
      if (!this.convertingMap[type]) {
        this.convertTifImage(type, url);
      }
      return "";
    },
    handleImageSourceChange(type, url) {
      this.$delete(this.conversionErrors, type);
      this.$set(this.convertedImageUrls, type, null);
      if (!url) {
        this.$set(this.convertingMap, type, false);
        return;
      }
      if (this.isTifUrl(url)) {
        this.convertTifImage(type, url);
      } else {
        this.$set(this.convertingMap, type, false);
        this.$set(this.convertedImageUrls, type, url);
      }
    },
    async convertTifImage(type, url) {
      if (!url || this.convertingMap[type]) {
        return;
      }
      this.$set(this.convertingMap, type, true);
      this.$delete(this.conversionErrors, type);
      try {
        const isMobile = window.innerWidth <= 768;

        const controller = new AbortController();
        const timeoutId = setTimeout(
          () => controller.abort(),
          isMobile ? 30000 : 15000
        );

        const response = await fetch(url, {
          cache: "no-store",
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.statusText}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        const ifds = UTIF.decode(arrayBuffer);
        if (!ifds.length) {
          throw new Error("No image data found in TIF file");
        }
        const firstImage = ifds[0];
        UTIF.decodeImage(arrayBuffer, firstImage);
        let { width, height } = firstImage;
        if (!width || !height) {
          throw new Error("Invalid TIF image size");
        }

        const maxCanvasSize = isMobile ? 2048 : 4096;
        let scale = 1;
        if (width > maxCanvasSize || height > maxCanvasSize) {
          scale = Math.min(maxCanvasSize / width, maxCanvasSize / height);
          width = Math.floor(width * scale);
          height = Math.floor(height * scale);
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          throw new Error("无法创建canvas上下文");
        }

        const imageData = ctx.createImageData(width, height);
        const rgba = UTIF.toRGBA8(firstImage);

        if (scale < 1) {
          const originalWidth = firstImage.width;
          const originalHeight = firstImage.height;
          const invScale = 1 / scale;

          for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
              const srcX = Math.min(
                Math.floor(x * invScale),
                originalWidth - 1
              );
              const srcY = Math.min(
                Math.floor(y * invScale),
                originalHeight - 1
              );
              const srcIdx = (srcY * originalWidth + srcX) * 4;
              const dstIdx = (y * width + x) * 4;
              imageData.data[dstIdx] = rgba[srcIdx];
              imageData.data[dstIdx + 1] = rgba[srcIdx + 1];
              imageData.data[dstIdx + 2] = rgba[srcIdx + 2];
              imageData.data[dstIdx + 3] = rgba[srcIdx + 3];
            }
          }
        } else {
          imageData.data.set(rgba);
        }

        ctx.putImageData(imageData, 0, 0);
        const dataUrl = canvas.toDataURL("image/png");

        canvas.width = 0;
        canvas.height = 0;

        // 使用$set确保响应式更新，并强制触发视图更新
        this.$set(this.convertedImageUrls, type, dataUrl);

        // 手机端：强制更新视图，确保图片显示
        const isMobileForUpdate = window.innerWidth <= 768;
        if (isMobileForUpdate) {
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      } catch (error) {
        console.error(`Failed to convert TIF image (${type}):`, error);
        this.$set(
          this.conversionErrors,
          type,
          (error && error.message) || "convert failed"
        );
        this.$set(this.convertedImageUrls, type, null);
      } finally {
        this.$set(this.convertingMap, type, false);
      }
    },
    /**
     * 供父组件 ReportPanel.vue 调用
     * 切换打印状态。
     * @param {boolean} isPrinting - 是否处于打印/导出状态
     */
    togglePrintingState(isPrinting) {
      this.isPrinting = isPrinting;
    },
    getChartInstances() {
      const instances = [];
      if (this.$refs.mtfValueChartOption_Chart) {
        instances.push(this.$refs.mtfValueChartOption_Chart.chart);
      }
      if (this.$refs.squareGrayScaleChartOption_Chart) {
        instances.push(this.$refs.squareGrayScaleChartOption_Chart.chart);
      }
      if (this.$refs.lsfGrayScaleChartOption_Chart) {
        instances.push(this.$refs.lsfGrayScaleChartOption_Chart.chart);
      }
      return instances;
    },
    /**
     * 确保所有图表都已渲染完成。
     */
    ensureChartsRendered() {
      const chartInstances = this.getChartInstances();
      if (!chartInstances || chartInstances.length === 0) {
        return Promise.resolve();
      }
      const promises = chartInstances.map(
        (chart) =>
          new Promise((resolve) => {
            // 'finished' 事件确保渲染完成
            const listener = () => {
              resolve();
              chart.off("finished", listener);
            };
            chart.on("finished", listener);
            // 超时保护
            setTimeout(() => {
              resolve();
              chart.off("finished", listener);
            }, 2000);
          })
      );
      return Promise.all(promises);
    },
    generateDistinctColors(count) {
      const colors = [];
      const goldenAngle = 137.5;
      for (let i = 0; i < count; i++) {
        const hue = (i * goldenAngle) % 360;
        colors.push(`hsl(${hue}, 85%, 50%)`);
      }
      return colors;
    },
    scrollToSection(sectionId) {
      const scrollContainer = this.$el.closest(
        ".detail-scrollbar .el-scrollbar__wrap"
      );
      if (!scrollContainer) return;

      if (sectionId === "top") {
        scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const targetElement = this.$el.querySelector(`#${sectionId}`);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    },
  },
};
</script>

<style scoped>
.custom-table ::v-deep .el-table__cell {
  padding: 8px 0;
}
.labeled-image-container {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.image-label-wrapper {
  padding: 0 5px;
  text-align: left;
}
.sharpness-report-detail-container {
  padding-right: 15px;
}
.report-header-images {
  display: flex;
  gap: 20px;
  margin-bottom: 25px;
  justify-content: center;
}
.image-wrapper {
  border: 1px solid #e6ebf5;
  border-radius: 6px;
  padding: 16px;
  background-color: #fcfcfc;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 160px;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}
.image-wrapper img {
  width: 100%;
  height: auto;
  max-height: 420px;
  object-fit: contain;
  border-radius: 4px;
  display: block;
}
.image-loading,
.image-error {
  width: 100%;
  text-align: center;
  color: #909399;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.image-loading .el-icon-loading {
  font-size: 24px;
  color: #409eff;
}
.image-error {
  color: #f56c6c;
}
.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin-top: 35px;
  margin-bottom: 20px;
}
.sub-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-top: 25px;
  margin-bottom: 15px;
  padding-left: 12px;
  border-left: 4px solid #00a0e9;
}
.analysis-placeholder {
  width: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}
.analysis-placeholder .el-icon-loading {
  font-size: 26px;
  color: #409eff;
}
.data-curve-image {
  width: 100%;
  height: auto;
  border-radius: 4px;
  display: block;
  max-width: 520px;
  margin: 10px auto;
  object-fit: contain;
}
.chart-container {
  width: 100%;
  margin-bottom: 20px;
  padding: 5px;
  border: 1px solid #e6ebf5;
  border-radius: 6px;
  box-sizing: border-box;
}
.chart-inner-wrapper {
  max-width: 100%;
  margin: 0 auto;
}
.echart {
  height: 500px;
  width: 100%;
}
.custom-table {
  margin-bottom: 20px;
}
.custom-table ::v-deep th {
  background-color: #f5f7fa !important;
  color: #606266;
  font-weight: 600;
}
.custom-table ::v-deep td,
.custom-table ::v-deep th {
  text-align: center;
}
@media (max-width: 768px) {
  .report-header-images {
    flex-direction: column;
  }
  .image-wrapper {
    padding: 15px;
  }
}
</style>
